const timelineMarkerGap = 12;

export type TimelineMarkerAlignment = 'start' | 'center' | 'end';

export interface TimelineMarkerAnchor {
    alignment: TimelineMarkerAlignment;
    offset: number;
}

export interface TimelineMarkerLayout {
    labelOffset: number;
    lane: number;
}

interface TimelineMarkerBounds extends TimelineMarkerLayout {
    alignment: TimelineMarkerAlignment;
    index: number;
    left: number;
    right: number;
}

function assignTimelineMarkerLanes(markerBounds: TimelineMarkerBounds[], firstLane: number, gap: number): number {
    const laneRightEdges: number[] = [];

    markerBounds
        .slice()
        .sort((first, second) => first.left - second.left || first.index - second.index)
        .forEach((marker) => {
            let lane = laneRightEdges.findIndex((rightEdge) => marker.left >= rightEdge + gap);

            if (lane === -1) {
                lane = laneRightEdges.length;
                laneRightEdges.push(marker.right);
            } else {
                laneRightEdges[lane] = marker.right;
            }

            marker.lane = firstLane + lane;
        });

    return firstLane + laneRightEdges.length;
}

export function calculateTimelineMarkerLayout(
    timelineMarkers: ReadonlyArray<TimelineMarkerAnchor>,
    labelWidths: ReadonlyArray<number>,
    timelineWidth: number,
    labelGap: number = timelineMarkerGap,
): TimelineMarkerLayout[] {
    if (timelineMarkers.length !== labelWidths.length) {
        throw new Error('Timeline marker and label counts must match.');
    }

    const availableWidth = Math.max(timelineWidth, 0);
    const gap = Math.max(labelGap, 0);
    const markerBounds: TimelineMarkerBounds[] = timelineMarkers.map((timelineMarker, index) => {
        const labelWidth = Math.max(labelWidths[index], 0);
        const markerOffset = Math.min(Math.max(timelineMarker.offset, 0), 100);
        const markerPosition = (markerOffset * availableWidth) / 100;
        let idealLeft = markerPosition - labelWidth / 2;

        if (timelineMarker.alignment === 'start') {
            idealLeft = markerPosition;
        } else if (timelineMarker.alignment === 'end') {
            idealLeft = markerPosition - labelWidth;
        }

        const left = Math.min(Math.max(idealLeft, 0), Math.max(availableWidth - labelWidth, 0));

        return {
            alignment: timelineMarker.alignment,
            index,
            labelOffset: left - markerPosition,
            lane: 0,
            left,
            right: left + labelWidth,
        };
    });
    const firstInternalLane = assignTimelineMarkerLanes(
        markerBounds.filter((marker) => marker.alignment !== 'center'),
        0,
        gap,
    );
    assignTimelineMarkerLanes(
        markerBounds.filter((marker) => marker.alignment === 'center'),
        firstInternalLane,
        gap,
    );

    return markerBounds
        .sort((first, second) => first.index - second.index)
        .map(({ labelOffset, lane }) => ({ labelOffset, lane }));
}
